import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Euro,
  Download,
  CheckCircle,
  XCircle,
  RotateCcw,
  Loader2,
  Clock,
  AlertCircle,
  Banknote,
  Send,
  FileText,
  Eye,
} from 'lucide-react';
import { exportDataset } from '../../lib/exportData';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Facture a valider', class: 'bg-amber-100 text-amber-700', icon: Clock },
  invoice_uploaded: { label: 'Facture a valider', class: 'bg-blue-100 text-blue-700', icon: FileText },
  validated: { label: 'Validee - A declencher', class: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  payment_pending: { label: 'En cours paiement', class: 'bg-indigo-100 text-indigo-700', icon: Euro },
  en_cours_paiement: { label: 'En cours paiement', class: 'bg-indigo-100 text-indigo-700', icon: Euro },
  paid: { label: 'Payee', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Renvoyee', class: 'bg-red-100 text-red-700', icon: AlertCircle },
};

export const AdminRHPayments = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [rejectDialog, setRejectDialog] = useState({ open: false, commission: null });
  const [rejectComment, setRejectComment] = useState('');

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      const response = await fetch(`${API}/admin/rh-commissions`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCommissions(data);
      }
    } catch (error) {
      console.error('Failed to fetch RH commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (commissionId) => {
    setProcessing(commissionId);
    try {
      const response = await fetch(`${API}/admin/rh-commissions/${commissionId}/validate`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Facture validee');
        fetchCommissions();
      } else {
        toast.error('Erreur lors de la validation');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleMoveToPayment = async (commissionId) => {
    setProcessing(commissionId);
    try {
      const response = await fetch(`${API}/admin/rh-commissions/${commissionId}/move-to-payment`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Paiement declenche');
        fetchCommissions();
      } else {
        toast.error('Erreur lors du declenchement');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handlePay = async (commissionId) => {
    setProcessing(commissionId);
    try {
      const response = await fetch(`${API}/admin/rh-commissions/${commissionId}/pay`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success('Commission marquee comme payee');
        fetchCommissions();
      } else {
        toast.error('Erreur lors du paiement');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      toast.error('Veuillez saisir un motif de renvoi');
      return;
    }
    const commId = rejectDialog.commission?.commission_id;
    setProcessing(commId);
    try {
      const response = await fetch(
        `${API}/admin/rh-commissions/${commId}/reject?comment=${encodeURIComponent(rejectComment)}`,
        { method: 'PUT', credentials: 'include' }
      );
      if (response.ok) {
        toast.success('Facture renvoyee avec commentaire');
        setRejectDialog({ open: false, commission: null });
        setRejectComment('');
        fetchCommissions();
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setProcessing(null);
    }
  };

  const downloadInvoice = async (commissionId, filename) => {
    setDownloading(commissionId);
    try {
      const response = await fetch(
        `${API}/rh/commissions/${commissionId}/download-invoice`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'facture.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Erreur de telechargement');
      }
    } catch (error) {
      toast.error('Erreur de telechargement');
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-slate-200 rounded" />
        <div className="h-96 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  // Treat both old and new statuses
  const isPendingValidation = (c) => ['pending', 'invoice_uploaded'].includes(c.status);
  const isValidated = (c) => c.status === 'validated';
  const isInPayment = (c) => ['payment_pending', 'en_cours_paiement'].includes(c.status);
  const isPaid = (c) => c.status === 'paid';

  // Stats
  const amount = (c) => c.commission_amount || c.amount || 0;
  const totalToValidate = commissions.filter(isPendingValidation).reduce((s, c) => s + amount(c), 0);
  const totalValidated = commissions.filter(isValidated).reduce((s, c) => s + amount(c), 0);
  const totalInPayment = commissions.filter(isInPayment).reduce((s, c) => s + amount(c), 0);
  const totalPaid = commissions.filter(isPaid).reduce((s, c) => s + amount(c), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-[Manrope]" data-testid="admin-rh-payments-title">
            Paiements RH
          </h1>
          <p className="text-slate-500 mt-1">Workflow complet : valider la facture → declencher → confirmer le paiement</p>
        </div>
        <Button variant="outline" onClick={() => exportDataset('rh-commissions', 'paiements_RH')} data-testid="export-rh-payments-btn">
          <Download className="w-4 h-4 mr-2" /> Exporter (CSV)
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">A valider</p>
              <p className="text-xl font-bold text-amber-600">{totalToValidate.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-purple-100">
              <CheckCircle className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">A declencher</p>
              <p className="text-xl font-bold text-purple-600">{totalValidated.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <Euro className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En paiement</p>
              <p className="text-xl font-bold text-indigo-600">{totalInPayment.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <Banknote className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Deja paye</p>
              <p className="text-xl font-bold text-emerald-600">{totalPaid.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="font-[Manrope]">Commissions RH</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Mois</TableHead>
                <TableHead>RH</TableHead>
                <TableHead>Commercial</TableHead>
                <TableHead>CA Vente</TableHead>
                <TableHead>Taux</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Facture</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {commissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                    Aucune commission RH
                  </TableCell>
                </TableRow>
              ) : (
                commissions.map((comm) => {
                  const StatusIcon = statusConfig[comm.status]?.icon || Clock;
                  const isLoading = processing === comm.commission_id;
                  return (
                    <TableRow key={comm.commission_id} data-testid={`rh-comm-row-${comm.commission_id}`}>
                      <TableCell className="font-medium">{comm.month}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{comm.rh_name}</p>
                          <p className="text-xs text-slate-400">{comm.rh_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>{comm.commercial_name || '-'}</TableCell>
                      <TableCell>{comm.sale_amount?.toLocaleString('fr-FR')} €</TableCell>
                      <TableCell>
                        <Badge variant="outline">{comm.commission_rate}%</Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-emerald-600">
                        {amount(comm).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                      </TableCell>
                      <TableCell>
                        {comm.invoice_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoice(comm.commission_id, comm.invoice_filename)}
                            disabled={downloading === comm.commission_id}
                            data-testid={`download-rh-invoice-${comm.commission_id}`}
                          >
                            {downloading === comm.commission_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Voir
                              </>
                            )}
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig[comm.status]?.class || 'bg-slate-100'} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[comm.status]?.label || comm.status}
                        </Badge>
                        {comm.rejection_comment && (
                          <p className="text-xs text-red-600 mt-1 max-w-[180px] truncate" title={comm.rejection_comment}>
                            {comm.rejection_comment}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* Step 1 : Valider / Renvoyer */}
                          {comm.invoice_id && isPendingValidation(comm) && (
                            <>
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleValidate(comm.commission_id)}
                                disabled={isLoading}
                                data-testid={`validate-rh-${comm.commission_id}`}
                              >
                                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                Valider
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-600 border-amber-300"
                                onClick={() => {
                                  setRejectComment('');
                                  setRejectDialog({ open: true, commission: comm });
                                }}
                                data-testid={`reject-rh-${comm.commission_id}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Renvoyer
                              </Button>
                            </>
                          )}

                          {/* Step 2 : Declencher le paiement */}
                          {isValidated(comm) && (
                            <Button
                              size="sm"
                              className="bg-indigo-600 hover:bg-indigo-700"
                              onClick={() => handleMoveToPayment(comm.commission_id)}
                              disabled={isLoading}
                              data-testid={`trigger-rh-${comm.commission_id}`}
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Euro className="w-4 h-4 mr-1" />}
                              Declencher paiement
                            </Button>
                          )}

                          {/* Step 3 : Confirmer le paiement */}
                          {isInPayment(comm) && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handlePay(comm.commission_id)}
                              disabled={isLoading}
                              data-testid={`confirm-rh-${comm.commission_id}`}
                            >
                              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4 mr-1" />}
                              Confirmer paiement
                            </Button>
                          )}

                          {isPaid(comm) && (
                            <span className="text-emerald-600 font-medium text-sm">Complete</span>
                          )}

                          {!comm.invoice_id && isPendingValidation(comm) && (
                            <span className="text-slate-400 text-sm">En attente facture RH</span>
                          )}

                          {comm.status === 'rejected' && (
                            <span className="text-slate-500 text-sm italic">En attente nouvelle facture</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renvoyer la facture RH</DialogTitle>
            <DialogDescription>
              La facture sera renvoyee a {rejectDialog.commission?.rh_name} pour correction.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Motif du renvoi *</Label>
              <Textarea
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Indiquez la raison du renvoi..."
                rows={4}
                data-testid="rh-reject-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, commission: null })}>
              Annuler
            </Button>
            <Button
              onClick={handleReject}
              disabled={!rejectComment.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="rh-confirm-reject-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Renvoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRHPayments;
